import get from "lodash-es/get";
import values from "lodash-es/values";
import { FetchError } from "ofetch";
import type { Pinia } from "pinia";
import isString from "lodash-es/isString";
import isObject from "lodash-es/isObject";
import isArray from "lodash-es/isArray";
import { Error_Codes } from "@/types/errors";

const toast = useToast();

export interface APIRequestError {
  data: null | Record<string, unknown>;
  statusCode: string;
  message: string;
  devMessage: string;
  errors: Record<string, unknown>;
}

type UseHandleErrorParam = {
  feedback?: string;
  toastId?: string;
  error: unknown;
  rethrow?: boolean;
  toastTimeout?: number;
};

export const useRequestErrorHandler = () => {
  return handleError;
};

export const isOnline = computed(() => useOnline().value);

// overloads
export async function handleError(
  handleErrorParam: UseHandleErrorParam,
): Promise<void>;
export function handleError(handleErrorParam: UseHandleErrorParam): void;
export async function handleError(handleErrorParam: UseHandleErrorParam) {
  // notifies about error and returns fallback (if requested)
  const {
    feedback,
    toastId,
    rethrow = false,
    error,
    toastTimeout = 2000,
  } = handleErrorParam;

  // swallow errors on server side
  if (import.meta.server) return;

  await notifyError({
    rethrow,
    error,
    feedback,
    toastId,
    toastTimeout,
  });
}

export async function notifyError({
  error,
  feedback,
  toastId,
  rethrow,
  toastTimeout,
}: UseHandleErrorParam) {
  if (import.meta.server) return;
  const nuxtApp = useNuxtApp();
  const sessionStore = useSessionStore(nuxtApp.$pinia as Pinia);

  toastId = toastId ?? `request-error-toast-${Math.random()}`;

  if (error instanceof FetchError) {
    const status = parseInt(String(get(error, "response.status", "0")), 10);
    const errResponse = error.data as APIRequestError;
    console.log({ errResponse, status });

    if (status === 404) {
      toast.error(feedback ?? "Page not found", {
        id: toastId,
        timeout: toastTimeout,
      });
      return;
    } else if (status >= 400) {
      if (status === Error_Codes.VALIDATION_ERROR) {
        toast.error(
          feedback ?? errResponse.errors?.message ?? errResponse.message,
          {
            id: toastId,
            timeout: toastTimeout,
          },
        );
      } else if (status === Error_Codes.AUTHENTICATION_ERROR) {
        await isModuleReady(sessionStore);
        toast.error(feedback ?? errResponse.message, {
          id: toastId,
          timeout: toastTimeout,
        });
        return;
      } else if (status === 403) {
        toast.error(feedback ?? errResponse.message, {
          id: toastId,
          timeout: toastTimeout ?? 4000,
        });
      } else {
        toast.error(feedback ?? errResponse.message, {
          id: toastId,
          timeout: toastTimeout,
        });
      }
    }
    // if error code beyond 400
    else {
      if (!isOnline.value) {
        toast.error(
          "Ooops.. An error occured while sending request.. PLease check your internet connection",
          {
            id: toastId,
            timeout: toastTimeout,
          },
        );
      } else if (!status) {
        // do nothing.. aborted request
      } else
        toast.error(feedback ?? errResponse?.message ?? error.message, {
          id: toastId,
          timeout: toastTimeout,
        });
    }
  }

  // if not a FetchError
  else {
    if (!isOnline.value) {
      console.log({ error });

      toast.error(
        "Ooops.. An error occured while sending request.. PLease check your internet connection",
        {
          id: toastId,
          timeout: toastTimeout,
        },
      );
    } else {
      toast.error(
        feedback ??
          "Ooops.. An error occured while sending request.. PLease check your internet connection",
        {
          id: toastId,
          timeout: toastTimeout,
        },
      );
    }
  }

  if (rethrow) {
    throw error;
  }
}

export enum ResponseStatusCodeConst {
  NOT_FOUND = "NOT_FOUND",
  BAD_REQUEST = "BAD_REQUEST",
  VALIDATION_FAILED = "VALIDATION_FAILED",
  SERVER_ERROR = "SERVER_ERROR",
  UNAUTHORIZED = "UNAUTHORIZED",
  PHONE_NOT_VERIFIED = "PHONE_NOT_VERIFIED",
  USER_BLOCKED = "USER_BLOCKED",
  SUCCESS = "SUCCESS",
  FORBIDDEN = "FORBIDDEN",
  TOO_MANY_REQUESTS = "TOO_MANY_REQUESTS",
  PAGE_NOT_FOUND = "PAGE_NOT_FOUND",
  NO_CONTENT = "NO_CONTENT",
  UNPROCESSABLE_ENTITY = "UNPROCESSABLE_ENTITY",
}

export type RequestErrorType = {
  property: string;
  constraints: Record<string, string>;
  children: RequestErrorType[];
};

export function getFirstErrorMessage<
  SENT_DATA_TYPE = Record<string, unknown>,
  RESPONSE_DATA_TYPE = unknown,
>(error: unknown, defaultErrorMessage = "An error occurred") {
  if (error instanceof FetchError) {
    // const status = parseInt(String(error.response?.status || "0"));

    const data = error.response?._data as {
      data: RESPONSE_DATA_TYPE;
      statusCode: ResponseStatusCodeConst;
      message: string;
      devMessage: string;
      errors?:
        | {
            target: SENT_DATA_TYPE;
            errors: RequestErrorType[] | string[];
          }
        | string[];
    };

    if (!data) {
      return defaultErrorMessage;
    }

    const getNestedErrorStr = (
      errors: RequestErrorType[] | string[],
    ): string => {
      const errorValue = errors.find((error) => !!error);
      if (isString(errorValue)) {
        return errorValue;
      }

      if (isObject(errorValue)) {
        if (
          errorValue.children &&
          isArray(errorValue.children) &&
          errorValue.children.length
        ) {
          const errorStr = getNestedErrorStr(errorValue.children);
          if (errorStr) {
            return errorStr;
          }
        }

        if (errorValue.constraints) {
          const errors = values(errorValue.constraints);
          if (errors.length) {
            return getNestedErrorStr(errors);
          }
        }
      }

      return "";
    };

    if (data.errors) {
      let errors: RequestErrorType[] | string[] = [];

      if (isArray(data.errors)) {
        errors = data.errors;
      } else if (isObject(data.errors)) {
        if ("target" in data.errors) {
          errors = data.errors.errors;
        } else {
          errors = values(errors) as RequestErrorType[] | string[];
        }
      }

      const errorStr = getNestedErrorStr(errors);
      if (errorStr) {
        return errorStr;
      }
    }

    return data.message || defaultErrorMessage;
  }

  return error instanceof Error
    ? error.message || defaultErrorMessage
    : defaultErrorMessage;
}
